import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Download,
  PictureAsPdf,
  Assessment,
  TrendingUp,
  School,
  EmojiEvents,
  CalendarMonth,
  CheckCircle,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Radar } from "react-chartjs-2";
import reportService from "../../services/reportService";
import { downloadBlob } from "../../utils/download";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

const defaultAcademicData = {
  overall: 0,
  attendance: 0,
  assignments: 0,
  exams: 0,
  participation: 0,
  subjects: [],
  achievements: [],
  improvements: [],
};

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState("academic");
  const [selectedPeriod, setSelectedPeriod] = useState("semester");
  const [academicData, setAcademicData] = useState(defaultAcademicData);
  const [availableReports, setAvailableReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await reportService.getStudentReport({
          from: selectedPeriod === "year" ? new Date().getFullYear() + "-01-01" : undefined,
          to: new Date().toISOString().slice(0, 10),
        });
        if (!mounted) return;
        const data = res?.data?.data ?? res?.data ?? {};
        if (data.academicData) {
          setAcademicData(data.academicData);
        }
        if (Array.isArray(data.availableReports)) {
          setAvailableReports(data.availableReports);
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load report");
        setAcademicData(defaultAcademicData);
        setAvailableReports([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, [selectedPeriod]);

  const subjectData = {
    labels: (academicData.subjects || []).map((s) => s.name),
    datasets: [
      {
        label: "Current Semester",
        data: (academicData.subjects || []).map((s) => s.score),
        backgroundColor: "rgba(25, 118, 210, 0.7)",
      },
    ],
  };

  const skillsData = {
    labels: [
      "Academics",
      "Attendance",
      "Assignments",
      "Exams",
      "Participation",
      "Discipline",
    ],
    datasets: [
      {
        label: "Performance",
        data: [
          academicData.overall || 0,
          academicData.attendance || 0,
          academicData.assignments || 0,
          academicData.exams || 0,
          academicData.participation || 0,
          academicData.attendance || 0,
        ],
        backgroundColor: "rgba(25, 118, 210, 0.2)",
        borderColor: "#1976d2",
        pointBackgroundColor: "#1976d2",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#1976d2",
      },
    ],
  };

  const handleDownload = (report) => {
    if (downloading) return;
    const from = selectedPeriod === "year" ? new Date().getFullYear() + "-01-01" : undefined;
    const to = new Date().toISOString().slice(0, 10);
    setDownloading(true);
    reportService
      .downloadStudentReportPdf({ ...(from ? { from } : {}), to })
      .then((res) => {
        downloadBlob(res.data, "student-report.pdf", "application/pdf");
      })
      .catch(() => {})
      .finally(() => setDownloading(false));
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Reports & Analytics
        </Typography>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading your report...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and download your academic reports
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={selectedReport}
                label="Report Type"
                onChange={(e) => setSelectedReport(e.target.value)}
              >
                <MenuItem value="academic">Academic Report</MenuItem>
                <MenuItem value="attendance">Attendance Report</MenuItem>
                <MenuItem value="progress">Progress Report</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="semester">Current Semester</MenuItem>
                <MenuItem value="year">Full Year</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Overall Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Overall Score",
            value: academicData.overall,
            icon: <Assessment />,
            color: "primary.main",
          },
          {
            label: "Attendance",
            value: academicData.attendance,
            icon: <CheckCircle />,
            color: "success.main",
          },
          {
            label: "Assignments",
            value: academicData.assignments,
            icon: <School />,
            color: "info.main",
          },
          {
            label: "Participation",
            value: academicData.participation,
            icon: <EmojiEvents />,
            color: "warning.main",
          },
        ].map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent sx={{ textAlign: "center" }}>
                <Avatar sx={{ bgcolor: `${stat.color}15`, mx: "auto", mb: 1 }}>
                  {React.cloneElement(stat.icon, { sx: { color: stat.color } })}
                </Avatar>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Subject Performance */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Subject-wise Performance Comparison
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={subjectData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "top" } },
                    scales: { y: { beginAtZero: true, max: 100 } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Skills Radar */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Overall Skills
              </Typography>
              <Box sx={{ height: 300 }}>
                <Radar
                  data={skillsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      r: {
                        angleLines: { display: true },
                        suggestedMin: 0,
                        suggestedMax: 100,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Achievements */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <EmojiEvents
                  sx={{ verticalAlign: "middle", mr: 1, color: "warning.main" }}
                />
                Recent Achievements
              </Typography>
              <List>
                {academicData.achievements.map((achievement, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar
                        sx={{ bgcolor: "warning.light", width: 36, height: 36 }}
                      >
                        <CheckCircle
                          sx={{ color: "warning.main", fontSize: 18 }}
                        />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={achievement.title}
                      secondary={achievement.date}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Areas of Improvement */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <TrendingUp
                  sx={{ verticalAlign: "middle", mr: 1, color: "info.main" }}
                />
                Areas of Improvement
              </Typography>
              <List>
                {academicData.improvements.map((improvement, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar
                        sx={{ bgcolor: "info.light", width: 36, height: 36 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "info.main",
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText primary={improvement} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Downloadable Reports */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            <Download sx={{ verticalAlign: "middle", mr: 1 }} />
            Downloadable Reports
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Generated On</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableReports.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PictureAsPdf sx={{ color: "#e53935" }} />
                        <Typography variant="body2" fontWeight={500}>
                          {report.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={report.type} size="small" />
                    </TableCell>
                    <TableCell>{report.size}</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <CalendarMonth
                          sx={{ fontSize: 16, color: "text.secondary" }}
                        />
                        {report.date}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownload(report)}
                        disabled={downloading}
                      >
                        {downloading ? <CircularProgress size={16} /> : "Download"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Reports;
